import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { betaApply } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";
import Brand from "@/components/Brand";
import { useAppVersion } from "@/lib/electron";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [ident, setIdent] = useState("");
  const [password, setPassword] = useState("");
  // Ticked by default in the desktop app, which is one pilot's own computer;
  // unticked in a browser, which may be shared.
  const [remember, setRemember] = useState(typeof window !== "undefined" && !!window.electronAPI);
  const [err, setErr] = useState("");
  // 403 from login is the closed beta turning a non-tester away.
  const [closedBeta, setClosedBeta] = useState(false);
  // The "apply" links show only while sign-up is open (2026-10-15).
  const { data: gate } = useQuery({ queryKey: ["beta-apply-open"], queryFn: betaApply.open, staleTime: 60_000, retry: false });
  const applyOpen = gate?.open === true;
  const [busy, setBusy] = useState(false);
  const appVersion = useAppVersion();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setClosedBeta(false);
    setBusy(true);
    try {
      await login(ident, password, remember);
      nav("/");
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
      setClosedBeta(e.response?.status === 403);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 text-foreground">
      {/* Hero */}
      <div className="hidden md:flex flex-col justify-between p-10 relative border-r border-border bg-card/40" data-testid="login-hero">
        <Brand size="lg" testid="login-logo" />

        <div>
          <div className="overline mb-3">Crew portal</div>
          <h1 className="text-5xl font-bold tracking-tight mb-4 max-w-md leading-[1.05]">
            File OFPs.
            <br />
            Fly the line.
            <br />
            <span className="text-primary">Report back.</span>
          </h1>
          <div className="acbar" />
          <p className="text-sm text-muted-foreground max-w-md leading-relaxed mt-6">
            The FlyVAC ACARS Companion. Sign in with your pilot ID, initialise your
            bid, and let SimConnect, FSUIPC or X-Plane's own network output
            stream your flight from MSFS, Prepar3D or X-Plane straight into the
            crew system.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-8 text-xs">
          <div>
            <div className="overline mb-1">Sim</div>
            <div className="font-mono">MSFS · P3D · XP12</div>
          </div>
          <div>
            <div className="overline mb-1">Bridge</div>
            {/* Not XPUIPC. X-Plane is reached over its own UDP network output
                (Settings → Network → Accept incoming connections), so there is
                no third-party bridge for a pilot to install. */}
            <div className="font-mono">SimConnect · FSUIPC · UDP</div>
          </div>
          <div>
            <div className="overline mb-1">Build</div>
            {/* The real build, as in the sidebar. Was a hardcoded "FlyVAC 5.0"
                that never changed between releases (2026-10-11). */}
            <div className="font-mono" data-testid="login-build">
              {appVersion ? `VACARS5 v${appVersion}` : "VACARS5 · Web"}
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm card-flat p-8" data-testid="login-form">
          <div className="mb-8">
            <div className="overline mb-2">Sign in</div>
            <h2 className="text-2xl font-bold tracking-tight">Pilot login</h2>
            <div className="acbar" />
            <p className="text-xs text-muted-foreground mt-4">
              Use your FlyVAC website credentials.
            </p>
          </div>

          <label className="block mb-4">
            <span className="overline block mb-1.5">Pilot ID or Email</span>
            <input
              type="text"
              required
              autoFocus
              value={ident}
              onChange={(e) => setIdent(e.target.value)}
              placeholder="1234, VAC001 or you@vacanada.org"
              data-testid="login-input-ident"
              className="field w-full px-3 py-2 text-sm font-mono"
            />
          </label>

          <label className="block mb-4">
            <span className="overline block mb-1.5">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="login-input-password"
              className="field w-full px-3 py-2 text-sm font-mono"
            />
          </label>

          <label className="flex items-center gap-2 mb-6 cursor-pointer select-none text-sm" data-testid="login-remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span>Remember me</span>
            <span className="text-xs text-muted-foreground">(stay signed in for 30 days)</span>
          </label>

          {err && (
            <div
              className="mb-4 rounded-md text-xs text-primary border border-primary/50 bg-primary/10 px-3 py-2 font-mono"
              data-testid="login-error"
            >
              {err}
              {closedBeta && applyOpen && (
                <Link to="/apply" className="block mt-2 underline font-bold" data-testid="login-apply">
                  Apply to join the beta →
                </Link>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full rounded-md bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest py-3 hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Authenticating…" : "Sign in"}
          </button>

          <p className="text-[11px] text-muted-foreground mt-6 leading-relaxed">
            Anti-cheat is active on all live sessions.
          </p>
          {applyOpen && (
            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
              Want to test VACARS5?{" "}
              <Link to="/apply" className="text-primary hover:underline" data-testid="login-apply-footer">
                Apply to join the beta
              </Link>
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
